import type { EventSourceMessage } from "@microsoft/fetch-event-source";

// Transport contract for ConversationStreamAgent, kept in its own module (no @ag-ui/client import) so
// callers can wire the stream synchronously at boot while the heavy SDK is lazy-loaded on demand.
export interface ConversationStreamHandlers {
    onmessage: (event: EventSourceMessage) => void;
    onclose: () => void;
    onerror: (error: unknown) => void;
    signal: AbortSignal;
}

// Opens the conversation SSE stream and drives the handlers per frame. Injected so the agent stays
// decoupled from AiApiClient and unit-testable.
export type ConversationStreamConnect = (handlers: ConversationStreamHandlers) => Promise<void>;

export interface FedConversationStream {
    connect: ConversationStreamConnect;
    feed: (message: EventSourceMessage) => void;
    close: () => void;
    error: (error: unknown) => void;
}

// A manual-feed transport for ConversationStreamAgent: the caller owns the real SSE stream
// (fetchEventSource) and forwards each frame via feed(), so the agent runs alongside the existing
// stream handler on a single stream instead of opening its own. Frames that arrive before the agent
// subscribes are buffered so no leading event (e.g. RUN_STARTED) is dropped.
export function createFedConversationStream(): FedConversationStream {
    let handlers: ConversationStreamHandlers | undefined;
    let resolveClose: (() => void) | undefined;
    let rejectClose: ((error: unknown) => void) | undefined;
    const pending: EventSourceMessage[] = [];
    // A close/error that arrives before the agent subscribes (runAgent's async onInitialize, or a lazy
    // import) must be replayed on connect too — otherwise the run never terminates.
    let terminal: { kind: "close" } | { kind: "error"; error: unknown } | undefined;

    const drain = () => {
        if (!handlers) {
            return;
        }
        for (const message of pending) {
            handlers.onmessage(message);
        }
        pending.length = 0;
        if (terminal?.kind === "error") {
            handlers.onerror(terminal.error);
            rejectClose?.(terminal.error);
        } else if (terminal?.kind === "close") {
            handlers.onclose();
            resolveClose?.();
        }
    };

    const connect: ConversationStreamConnect = handlerSet =>
        new Promise<void>((resolve, reject) => {
            handlers = handlerSet;
            resolveClose = resolve;
            rejectClose = reject;
            drain();
        });

    return {
        connect,
        feed: message => {
            if (handlers) {
                handlers.onmessage(message);
            } else {
                pending.push(message);
            }
        },
        close: () => {
            if (handlers) {
                handlers.onclose();
                resolveClose?.();
            } else {
                terminal ??= { kind: "close" };
            }
        },
        error: error => {
            if (handlers) {
                handlers.onerror(error);
                rejectClose?.(error);
            } else {
                terminal ??= { kind: "error", error };
            }
        }
    };
}
